import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import authService, { User } from './services/auth';
import Landing from './components/Landing';
import About from './components/About';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import ProtectedRoute from './components/ProtectedRoute';

function AppContent() {
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [authenticated, setAuthenticated] = useState(false);
  const location = useLocation();

  const checkAuth = async () => {
    try {
      const currentUser = await authService.getCurrentUser();
      if (currentUser) {
        setUser(currentUser);
        setAuthenticated(true);
      } else {
        setAuthenticated(false);
        setUser(null);
      }
    } catch (error) {
      console.error('Error checking authentication:', error);
      setAuthenticated(false);
      setUser(null);
    } finally {
      setCheckingAuth(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  // Re-check auth when navigating to protected routes
  useEffect(() => {
    if (location.pathname !== '/login' && location.pathname !== '/' && location.pathname !== '/about') {
      const token = authService.getToken();
      if (token && !user && !checkingAuth) {
        // Token exists but user state not set, re-check auth
        checkAuth();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleLogout = () => {
    authService.logout();
    setAuthenticated(false);
    setUser(null);
  };

  // Show loading while checking authentication
  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/about" element={<About />} />
      <Route 
        path="/login" 
        element={
          authenticated ? <Navigate to="/overview" replace /> : <Login />
        } 
      />
      
      {/* Protected Routes - Single Dashboard route handles all nested routes */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            {user ? <Dashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" replace />}
          </ProtectedRoute>
        }
      />
      
      {/* Catch all - redirect to home if not authenticated, overview if authenticated */}
      <Route path="*" element={<Navigate to={authenticated ? "/overview" : "/"} replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
